"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, User } from "lucide-react";
import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { Button } from "../../elements/button/button";
import { Text } from "../../elements/text/text";
import { UserFormField } from "../user-form-modal/user-form-field";
import {
  type UserProfileFormData,
  userCardProfileSchema,
} from "./user-card-profile.schema";

export type UserRole =
  | "ADMIN"
  | "SUB_ADMIN"
  | "SHOP_EMPLOYEE"
  | "SHOP_VIEWER"
  | "EMPLOYEE"
  /** Sales Rep / Rider Rep acting on behalf of a dealer (BOB, COMPB2B-2529). */
  | "BOB"
  | "UNKNOWN";

type BaseUserCardProfileProps = {
  userRole?: UserRole;
  microcopies: {
    title: string;
    subtitle: string;
    customerIdLabel: string;
    firstNameLabel: string;
    lastNameLabel: string;
    emailLabel: string;
  };
  data: {
    customerId: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  isPending?: boolean;
  onSubmit?: (data: UserProfileFormData) => void | Promise<void>;
};

type ReadonlyVariantProps = BaseUserCardProfileProps & {
  variant?: "readonly";
  microcopies: BaseUserCardProfileProps["microcopies"] & {
    footer: string;
  };
};

type EditableVariantProps = BaseUserCardProfileProps & {
  variant: "editable";
  onEdit: () => void;
};

type FormVariantProps = BaseUserCardProfileProps & {
  variant: "form";
  microcopies: BaseUserCardProfileProps["microcopies"] & {
    saveButtonLabel: string;
    customerIdPlaceholder?: string;
    firstNamePlaceholder?: string;
    lastNamePlaceholder?: string;
    emailPlaceholder?: string;
  };
  onSubmit: (data: UserProfileFormData) => void | Promise<void>;
};

export type UserCardProfileProps =
  | ReadonlyVariantProps
  | EditableVariantProps
  | FormVariantProps;

/**
 * UserCardProfile - Displays user profile information in a card format
 * Supports both readonly display and editable form variants
 */
export function UserCardProfile(props: UserCardProfileProps): ReactNode {
  const variant = props.variant || "readonly";

  // Form setup for form variant
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<UserProfileFormData>({
    // biome-ignore lint/suspicious/noExplicitAny: -
    resolver: zodResolver(userCardProfileSchema as any),
    values: props.data,
  });

  const canEditNameAndEmail =
    props.userRole !== "ADMIN" && props.userRole !== "UNKNOWN";

  const onSubmit = async (data: UserProfileFormData) => {
    if (variant === "form") {
      await (props as FormVariantProps).onSubmit(data);
      reset(data);
    }
  };

  if (variant === "readonly") {
    return (
      <div className="flex flex-col gap-6 rounded-md bg-white p-4">
        {/* Header Section */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <User className="h-6 w-6 text-zinc-500" />
            <Text variant="h4">{props.microcopies.title}</Text>
          </div>
          <div className="flex flex-col">
            <Text color="black" variant="body-md">
              {props.microcopies.subtitle}
            </Text>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            {/* Customer ID */}
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1">
                <Text variant="body-sm-bold">
                  {props.microcopies.customerIdLabel}
                </Text>
              </div>
              <div className="flex items-center">
                <Text variant="body-sm">{props.data.customerId}</Text>
              </div>
            </div>

            {/* First Name */}
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1">
                <Text variant="body-sm-bold">
                  {props.microcopies.firstNameLabel}
                </Text>
              </div>
              <div className="flex items-center">
                <Text variant="body-sm">{props.data.firstName}</Text>
              </div>
            </div>

            {/* Last Name */}
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1">
                <Text variant="body-sm-bold">
                  {props.microcopies.lastNameLabel}
                </Text>
              </div>
              <div className="flex items-center">
                <Text variant="body-sm">{props.data.lastName}</Text>
              </div>
            </div>

            {/* Email */}
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1">
                <Text variant="body-sm-bold">
                  {props.microcopies.emailLabel}
                </Text>
              </div>
              <div className="flex items-center">
                <Text variant="body-sm">{props.data.email}</Text>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Section */}
        <div className="flex items-center gap-6">
          <Text variant="body-md">
            {(props as ReadonlyVariantProps).microcopies.footer}
          </Text>
        </div>
      </div>
    );
  }

  if (variant === "editable") {
    const editableProps = props as EditableVariantProps;

    return (
      <div className="flex flex-col gap-6 rounded-md bg-white p-4">
        {/* Header Section */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <User className="h-6 w-6 text-muted-foreground" />
            <Text variant="h4">{props.microcopies.title}</Text>
          </div>
          <div className="flex flex-col">
            <Text color="black" variant="body-md">
              {props.microcopies.subtitle}
            </Text>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end">
            {/* Customer ID */}
            <div className="relative flex flex-1 flex-col gap-2">
              <div className="flex flex-col gap-1">
                <Text variant="body-sm-bold">
                  {props.microcopies.customerIdLabel}
                </Text>
              </div>
              <div className="flex items-center">
                <Text variant="body-md">{props.data.customerId}</Text>
              </div>
              {/* Edit button - absolute on mobile, flex on desktop */}
              {props.userRole !== "ADMIN" && props.userRole !== "UNKNOWN" && (
                <button
                  className="absolute top-0 right-0 flex h-8 w-8 items-center justify-center sm:hidden"
                  onClick={editableProps.onEdit}
                  type="button"
                >
                  <Pencil className="h-5 w-5 text-neutral-600" />
                </button>
              )}
            </div>

            {/* First Name */}
            <div className="flex flex-1 flex-col gap-2">
              <div className="flex flex-col gap-1">
                <Text variant="body-sm-bold">
                  {props.microcopies.firstNameLabel}
                </Text>
              </div>
              <div className="flex items-center">
                <Text variant="body-md">{props.data.firstName}</Text>
              </div>
            </div>

            {/* Last Name */}
            <div className="flex flex-1 flex-col gap-2">
              <div className="flex flex-col gap-1">
                <Text variant="body-sm-bold">
                  {props.microcopies.lastNameLabel}
                </Text>
              </div>
              <div className="flex items-center">
                <Text variant="body-md">{props.data.lastName}</Text>
              </div>
            </div>

            {/* Email */}
            <div className="flex flex-1 flex-col gap-2">
              <div className="flex flex-col gap-1">
                <Text variant="body-sm-bold">
                  {props.microcopies.emailLabel}
                </Text>
              </div>
              <div className="flex items-center">
                <Text variant="body-md">{props.data.email}</Text>
              </div>
            </div>

            {/* Edit button - visible on desktop only */}
            {props.userRole !== "ADMIN" && props.userRole !== "UNKNOWN" && (
              <button
                className="hidden h-10 w-8 items-center justify-end sm:flex"
                onClick={editableProps.onEdit}
                type="button"
              >
                <Pencil className="h-5 w-5 text-neutral-600" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Form variant
  const formProps = props as FormVariantProps;

  return (
    <form
      className="flex flex-col gap-6 rounded-md bg-white p-4"
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* Header Section */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <User className="h-6 w-6 text-muted-foreground" />
          <Text variant="h4">{props.microcopies.title}</Text>
        </div>
        <div className="flex flex-col">
          <Text color="black" variant="body-md">
            {props.microcopies.subtitle}
          </Text>
        </div>
      </div>

      {/* Content Section - Form Fields */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between">
          {/* Customer ID - Disabled */}
          <UserFormField
            className="flex flex-1 flex-col gap-1"
            disabled={true}
            id="customerId"
            label={formProps.microcopies.customerIdLabel}
            placeholder={formProps.microcopies.customerIdPlaceholder}
            registerProps={register("customerId")}
          />

          {/* First Name - Required */}
          <UserFormField
            className="flex flex-1 flex-col gap-1"
            disabled={!canEditNameAndEmail}
            error={errors.firstName}
            id="firstName"
            label={formProps.microcopies.firstNameLabel}
            placeholder={formProps.microcopies.firstNamePlaceholder}
            registerProps={register("firstName")}
            required={true}
          />

          {/* Last Name - Required */}
          <UserFormField
            className="flex flex-1 flex-col gap-1"
            disabled={!canEditNameAndEmail}
            error={errors.lastName}
            id="lastName"
            label={formProps.microcopies.lastNameLabel}
            placeholder={formProps.microcopies.lastNamePlaceholder}
            registerProps={register("lastName")}
            required={true}
          />

          {/* Email - Required */}
          <UserFormField
            className="flex flex-1 flex-col gap-1"
            disabled={!canEditNameAndEmail}
            error={errors.email}
            id="email"
            label={formProps.microcopies.emailLabel}
            placeholder={formProps.microcopies.emailPlaceholder}
            registerProps={register("email")}
            required={true}
            type="email"
          />
        </div>
      </div>

      {/* Footer Section - Submit Button */}
      <div className="flex items-center gap-2">
        <Button
          disabled={isSubmitting || !isDirty}
          loading={isSubmitting}
          size="sm"
          type="submit"
        >
          {formProps.microcopies.saveButtonLabel}
        </Button>
      </div>
    </form>
  );
}
